/**
 * Request correlation metadata carried through Algolia's raw response. The
 * SearchResults wrapper retains custom raw-result keys, unlike `_state`,
 * which the helper may optimistically rewrite after dispatch.
 */
export const ALGOLIA_REQUEST_STAMP = "__bookaclassicRequest";

export interface AlgoliaRequestStamp {
  indexName: string;
  params: unknown;
}

type SearchRequestLike = Record<string, unknown>;

function isRecord(value: unknown): value is SearchRequestLike {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

/** Extract requests sent through either the v4-style array or v5 object API. */
export function extractAlgoliaRequests(args: readonly unknown[]): SearchRequestLike[] {
  const first = args[0];
  if (Array.isArray(first)) return first.filter(isRecord);
  if (!isRecord(first)) return [];
  if (Array.isArray(first.requests)) return first.requests.filter(isRecord);
  return typeof first.indexName === "string" ? [first] : [];
}

function cloneRequestValue(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(cloneRequestValue);
  if (!isRecord(value)) return value;
  return Object.fromEntries(
    Object.entries(value).map(([key, nested]) => [key, cloneRequestValue(nested)]),
  );
}

function requestParams(request: SearchRequestLike): unknown {
  if (Object.prototype.hasOwnProperty.call(request, "params") && request.params !== undefined) {
    return request.params;
  }
  // Algolia v5 SearchQuery objects place search parameters alongside
  // `indexName`; v4 InstantSearch requests wrap those parameters in `params`.
  const { indexName: _indexName, type: _type, params: _params, ...params } = request;
  return params;
}

/** Snapshot request identity and parameters before awaiting the network call. */
export function captureAlgoliaRequestStamps(requests: readonly SearchRequestLike[]): AlgoliaRequestStamp[] {
  return requests.flatMap((request) =>
    typeof request.indexName === "string"
      ? [{ indexName: request.indexName, params: cloneRequestValue(requestParams(request)) }]
      : [],
  );
}

/** Clone a response envelope and attach its matching dispatch stamp per result. */
export function stampAlgoliaSearchResponse<T>(response: T, requests: readonly AlgoliaRequestStamp[]): T {
  if (!isRecord(response) || !Array.isArray(response.results)) return response;
  return {
    ...response,
    results: response.results.map((rawResult, index) => {
      if (!isRecord(rawResult) || !requests[index]) return rawResult;
      return { ...rawResult, [ALGOLIA_REQUEST_STAMP]: requests[index] };
    }),
  } as T;
}

function normalizeForComparison(value: unknown): unknown {
  if (Array.isArray(value)) return value.map((item) => item === undefined ? null : normalizeForComparison(item));
  if (!isRecord(value)) return value;
  return Object.fromEntries(
    Object.keys(value)
      .filter((key) => value[key] !== undefined)
      .sort()
      .map((key) => [key, normalizeForComparison(value[key])]),
  );
}

/** Canonical object-key order; arrays intentionally retain their order. */
export function canonicalAlgoliaParams(value: unknown): string {
  return JSON.stringify(normalizeForComparison(value) ?? {});
}

/**
 * Insights can inject its analytics userToken into dispatch params without
 * adding it to the helper's query state. It must not keep completed hits pending.
 * Retain every search parameter so stale filters, location and pages still wait.
 */
export function canonicalAlgoliaHitsParams(value: unknown): string {
  if (!isRecord(value)) return canonicalAlgoliaParams(value);
  const { userToken: _analyticsUserToken, ...searchParams } = value;
  return canonicalAlgoliaParams(searchParams);
}
